"use client";

import { useEffect, useRef, useState } from "react";

/** Splits "10k+" → { target: 10, suffix: "k+", decimals: 0 }, "4.5" → { 4.5, "", 1 } */
function parseValue(value: string) {
  const match = value.match(/^\s*([\d.,]+)(.*)$/);
  if (!match) return { target: 0, suffix: value, decimals: 0 };
  const numStr = match[1].replace(/,/g, "");
  const target = parseFloat(numStr) || 0;
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return { target, suffix: match[2], decimals };
}

export default function CountUp({
  value,
  className = "",
  duration = 1600,
}: {
  value: string;
  className?: string;
  duration?: number;
}) {
  const { target, suffix, decimals } = parseValue(value);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced-motion: jump straight to the final value.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(target);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting || started.current) return;
        started.current = true;

        const start = performance.now();
        const animate = (now: number) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
          setDisplay(target * eased);
          if (progress < 1) requestAnimationFrame(animate);
          else setDisplay(target);
        };
        requestAnimationFrame(animate);
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  const formatted =
    decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString();

  return (
    <span ref={ref} className={className}>
      {formatted}
      {suffix}
    </span>
  );
}
